import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link } from "gatsby";
import PageWrapper from "../components/PageWrapper";
import Instructions from "../components/Instructions/Instructions";
import { useAuth } from "../context/AuthContext";
import { subscribeUserNotifications, markNotificationsRead } from "../lib/firebase";

const Wrap = styled.section`
  background: var(--nd-black);
  min-height: 100vh;
  padding: 128px 24px 96px;
  color: var(--nd-text-primary);
  font-family: 'Space Grotesk', sans-serif;
`;

const Inner = styled.div`
  max-width: 720px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin: 0 0 12px;
`;

const Title = styled.h1`
  font-size: 40px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.02em;
  margin: 0 0 24px;
`;

const ToolBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
`;

const MarkAllBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: transparent;
  color: var(--nd-text-primary);
  border: 1px solid var(--nd-border-visible);
  border-radius: 999px;
  padding: 6px 14px;
  cursor: pointer;
  &:hover { border-color: var(--nd-text-display); }
`;

const Item = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border: 1px solid var(--nd-border);
  border-left: 3px solid ${({ unread }) => (unread ? "var(--nd-accent, #d71921)" : "var(--nd-border)")};
  border-radius: 6px;
  padding: 14px 16px;
  margin-bottom: 8px;
`;

const ItemLink = styled(Link)`
  flex: 1;
  text-decoration: none;
  color: inherit;
  &:hover .nd-title { color: var(--nd-text-primary); }
`;

const ItemTitle = styled.div`
  color: var(--nd-text-display);
  font-size: 14px;
  margin-bottom: 4px;
`;

const ItemBody = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  color: var(--nd-text-secondary);
`;

const ItemDate = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin-top: 6px;
`;

const MarkBtn = styled.button`
  flex: 0 0 auto;
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: transparent;
  color: var(--nd-text-secondary);
  border: 1px solid var(--nd-border-visible);
  border-radius: 999px;
  padding: 4px 10px;
  cursor: pointer;
  &:hover { border-color: var(--nd-text-display); color: var(--nd-text-display); }
`;

const Empty = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  text-align: center;
  margin: 64px 0;
`;

const SignInLine = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  color: var(--nd-text-secondary);
  margin-top: 24px;
`;

const fmtDate = (ts) => {
  const ms = ts && ts.toMillis ? ts.toMillis() : null;
  if (!ms) return "";
  return new Date(ms).toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

const NotificationsPage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!user || !user.uid) return undefined;
    const unsub = subscribeUserNotifications(user.uid, setItems);
    return () => unsub();
  }, [user]);

  const markOne = (id) => {
    if (!user || !user.uid) return;
    markNotificationsRead(user.uid, [id]).catch(() => {});
  };
  const markAll = () => {
    if (!user || !user.uid) return;
    const ids = items.filter((n) => !n.read).map((n) => n.id);
    if (ids.length) markNotificationsRead(user.uid, ids).catch(() => {});
  };

  if (!user) {
    return (
      <PageWrapper darkTheme>
        <Wrap>
          <Inner>
            <Kicker>Profile · Notifications</Kicker>
            <Title>Notifications</Title>
            <SignInLine>Sign in to see your notifications.</SignInLine>
          </Inner>
        </Wrap>
      </PageWrapper>
    );
  }

  const unreadCount = items.filter((n) => !n.read).length;

  return (
    <PageWrapper darkTheme>
      <Wrap>
        <Inner>
          <Kicker>Profile · Notifications</Kicker>
          <Title>Notifications</Title>

          <Instructions label="how to use notifications">
            <h4>What this is</h4>
            <p>
              Everything that needs your attention lands here — a lead replied,
              a lead was won, a lab you finished, a change to your role.
            </p>
            <h4>Reading them</h4>
            <ul>
              <li>A red left-edge marks a notification as <b>unread</b>.</li>
              <li>Click a notification to jump to what it's about (a thread, a lead, your profile).</li>
              <li>Hit <b>Mark read</b> on one, or <b>Mark all read</b> up top, to clear the badge on your avatar.</li>
            </ul>
            <h4>The avatar badge</h4>
            <p>
              The number on your profile picture is your unread count. Marking
              notifications read here clears it.
            </p>
          </Instructions>

          {unreadCount > 0 && (
            <ToolBar>
              <MarkAllBtn type="button" onClick={markAll}>Mark all read ({unreadCount})</MarkAllBtn>
            </ToolBar>
          )}

          {items.length === 0 && <Empty>nothing yet</Empty>}

          {items.map((n) => (
            <Item key={n.id} unread={n.read ? 0 : 1}>
              <ItemLink to={n.link || "/profile"}>
                <ItemTitle className="nd-title">{n.title}</ItemTitle>
                <ItemBody>{n.body}</ItemBody>
                {fmtDate(n.createdAt) && <ItemDate>{fmtDate(n.createdAt)}</ItemDate>}
              </ItemLink>
              {!n.read && (
                <MarkBtn type="button" onClick={() => markOne(n.id)}>Mark read</MarkBtn>
              )}
            </Item>
          ))}
        </Inner>
      </Wrap>
    </PageWrapper>
  );
};

export default NotificationsPage;
